import { FC, PropsWithChildren, ReactElement, useMemo } from 'react';

import { RenderingProvider, useCms, useContent } from '@eneco/cms';
import { getStaticPrefix } from '@eneco/common/env';
import { StretchBucketSkeleton } from '@eneco/components/StretchBucket';
import { TrackedNotificationBox } from '@eneco/components/TrackedNotificationBox/TrackedNotificationBox';
import { TrackViewComponentWithScopeAndVariant } from '@eneco/components/TrackViewComponentWithScopeAndVariant/TrackViewComponentWithScopeAndVariant';
import useDC from '@eneco/dc/useDC';
import { useCustomerProfileAccount } from '@eneco/hooks/profile';
import { I18nProvider, useTranslation } from '@eneco/i18n';
import { Box, Card, Image, Stack, Text, TextLink } from '@eneco/sparky';
import { useMediaQuery } from '@eneco/sparky/hooks';
import { NotificationBoxProps } from '@eneco/sparky/types';
import { SensitiveContentComponent } from '@eneco/tracking';

import type { AccountCardContent } from './account-card-content.types';
import { CustomLabels } from './types';
import AccountSwitchDialog from '../AccountCard/AccountSwitchDialog';

type AccountCardProps = {
  scope?: string;
  compactLayout?: boolean;
  usedInsideCard?: boolean;
  customLabels?: CustomLabels;
  onAccountSwitch?: (accountId: number) => void;
};

/**
 * What the component registry spreads onto AccountCard when the CMS places it on
 * a page. Apps that import AccountCard directly pass none of these and get an
 * empty rendering, which is what they already had: `compactLayout` is the only
 * field read here, and no parent rendering carries one.
 */
type AccountCardCmsProps = {
  fields?: unknown;
  params?: unknown;
  componentName?: string;
  uid?: string;
};

interface AccountCardContentWrapperProps extends PropsWithChildren {
  usedInsideCard?: boolean;
}

const AccountCard: FC<AccountCardProps & AccountCardCmsProps> = ({
  scope,
  compactLayout,
  usedInsideCard,
  customLabels,
  onAccountSwitch,
  fields,
  params,
  componentName,
  uid,
}) => {
  const { label } = useDC();
  const { adapter } = useCms();
  const rendering = useMemo(
    () => adapter.toRendering({ componentName: componentName ?? 'AccountCard', uid, fields, params }),
    [adapter, componentName, uid, fields, params],
  );

  return (
    <RenderingProvider rendering={rendering}>
      <I18nProvider dictionary={locale => import(`./content/${label}/${locale}.json`)}>
        <AccountCardComponent
          scope={scope}
          compactLayout={compactLayout}
          customLabels={customLabels}
          usedInsideCard={usedInsideCard}
          onAccountSwitch={() => onAccountSwitch?.(Number(scope))}
        />
      </I18nProvider>
    </RenderingProvider>
  );
};

const AccountCardComponent: FC<AccountCardProps> = ({
  scope,
  compactLayout,
  customLabels,
  usedInsideCard,
  onAccountSwitch,
}) => {
  const { compactLayout: compactLayoutFromCms } = useContent<AccountCardContent>();
  const { t, isLoading: isTranslationsLoading } = useTranslation();
  const { label } = useDC();
  const {
    data: profile,
    isLoading: isProfileLoading,
    currentAccount,
    error: profileError,
  } = useCustomerProfileAccount({ filterInactive: true });

  const mobileLayoutBecauseOfScreenSize = !useMediaQuery('md');
  const mobileLayoutApplied = !!compactLayoutFromCms || compactLayout || mobileLayoutBecauseOfScreenSize;

  if (isTranslationsLoading) {
    return <StretchBucketSkeleton height={48} singleSkeleton />;
  }

  if (isProfileLoading) {
    return (
      <StretchBucketSkeleton label={customLabels?.loadingLabel ?? t('loadingLabel.value')} height={48} singleSkeleton />
    );
  }

  const accounts = profile?.accounts;

  if (!accounts || accounts.length <= 1) {
    return null;
  }

  const getTrackingVariant = () => {
    if (profileError) return 'error';
    return 'meerdere aansluitadressen';
  };

  type variantTypes = Exclude<NotificationBoxProps['variant'], undefined>;
  const variant = customLabels?.errorNotification?.variant ?? (t('errorNotification.value.variant') as variantTypes);

  if (profileError || !currentAccount || !profile) {
    return (
      <AccountCardContentWrapper>
        <TrackedNotificationBox
          isAlert
          variant={variant}
          title={customLabels?.errorNotification?.title ?? t('errorNotification.value.title')}
          text={customLabels?.errorNotification?.content ?? t('errorNotification.value.content')}
        />
      </AccountCardContentWrapper>
    );
  }

  const address = currentAccount?.address;
  const addressLine = `${address?.street || ''} ${address?.houseNumber || ''}${address?.houseNumberSuffix || ''}, ${
    address?.city || ''
  }`;

  return (
    <TrackViewComponentWithScopeAndVariant
      scope={scope}
      type="aansluitadreswidget"
      label="Aansluitadres"
      variant={getTrackingVariant()}>
      <AccountCardContentWrapper usedInsideCard={usedInsideCard}>
        <Image src={`${getStaticPrefix()}/images/${label}/accountcard/address.svg`} alt="" maxHeight="48px" />
        <Stack.Item grow>
          <Stack direction={mobileLayoutApplied ? 'column' : 'row'} gap={mobileLayoutApplied ? '1' : '6'}>
            <Text weight="bold">
              <span role="heading" aria-level={2}>
                {customLabels?.title ?? t('title.value')}
              </span>
            </Text>
            <Stack.Item grow={!mobileLayoutApplied}>
              <Stack
                alignY="center"
                direction={mobileLayoutApplied ? 'column' : 'row'}
                gap={mobileLayoutApplied ? '3' : '6'}>
                <Stack.Item grow>
                  <SensitiveContentComponent>
                    <Text>{addressLine}</Text>
                  </SensitiveContentComponent>
                  <SensitiveContentComponent>
                    <Text color="textLowEmphasis">
                      {`${
                        customLabels?.referenceNumberLabel ?? t('referenceNumberLabel.value')
                      } ${profile.customerId}-${currentAccount.accountId}`}
                    </Text>
                  </SensitiveContentComponent>
                </Stack.Item>
                <AccountSwitchDialog
                  heading={customLabels?.modalDialog?.title ?? t('modalDialog.value.title')}
                  subheading={customLabels?.modalDialog?.subheading ?? t('modalDialog.value.subheading')}
                  content={customLabels?.modalDialog?.content ?? t('modalDialog.value.content')}
                  submit={customLabels?.modalDialog?.submitButtonText ?? t('modalDialog.value.submitButtonText')}
                  customerId={profile.customerId}
                  accounts={accounts}
                  selectedAccountId={currentAccount.accountId}
                  trigger={
                    <TextLink emphasis="high">
                      {customLabels?.modalDialog?.triggerText ?? t('modalDialog.value.triggerText')}
                    </TextLink>
                  }
                  onAccountSwitch={onAccountSwitch}
                />
              </Stack>
            </Stack.Item>
          </Stack>
        </Stack.Item>
      </AccountCardContentWrapper>
    </TrackViewComponentWithScopeAndVariant>
  );
};

const ConditionalWrapper: FC<{
  condition: boolean;
  wrapper: (children: ReactElement) => ReactElement;
  children: ReactElement;
}> = ({ condition, wrapper, children }) => (condition ? wrapper(children) : children);

const wrapInCard = (children: ReactElement) => <Card>{children}</Card>;

const AccountCardContentWrapper: FC<AccountCardContentWrapperProps> = ({ children, usedInsideCard }) => {
  const backgroundColor = usedInsideCard ? 'backgroundSecondary' : undefined;
  const borderRadius = usedInsideCard ? 'm' : 'none';

  return (
    <ConditionalWrapper condition={!usedInsideCard} wrapper={wrapInCard}>
      <Box padding="6" backgroundColor={backgroundColor} borderRadius={borderRadius}>
        <Stack direction="row" gap="6">
          {children}
        </Stack>
      </Box>
    </ConditionalWrapper>
  );
};

export default AccountCard;
